import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardPageFormBody,
  DashboardPageFormToggleSection,
  DashboardTableMultiSelectFilter,
  buildDashboardDataTableColumns,
  useDashboardDataTableClientPage,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { DashboardSelectionDialog } from '../../../../../../components/DashboardSelectionDialog';
import { countByStatus, manageDialogFetchSize } from '../../../../../../lib/clientSelectionList';
import {
  createDealAuctionTypeColumn,
  createDealBidFloorColumn,
  createDealFlightDatesColumn,
  createDealIdColumn,
  createDealIntegrationsColumn,
  createDealStatusColumn,
  createDealUpdatedColumn,
} from '../../../../deals/components/DealSummaryColumns';
import { useDealsQuery } from '../../../../deals/hooks';
import {
  dealStatusLabel,
  defaultDealsFilter,
  DealSortDirection,
  DealSortProperty,
  DealStatus,
  type Deal,
} from '../../../../deals/model';
import type { IntegrationFormValue } from '../../../integrationFormSchema';

const lookupFilter = {
  ...defaultDealsFilter,
  searchTerm: '',
  statuses: [] as DealStatus[],
  page: 0,
  size: manageDialogFetchSize,
  sortProperty: DealSortProperty.Name,
  sortDirection: DealSortDirection.Asc,
};

export function IntegrationPmpFields() {
  'use no memo';
  const { control, setValue } = useFormContext<IntegrationFormValue>();
  const enabled = useWatch({ control, name: 'pmpEnabled' });
  const selected = useWatch({ control, name: 'deals' });
  const [manageOpen, setManageOpen] = React.useState(false);
  const [statuses, setStatuses] = React.useState<DealStatus[]>([]);
  const query = useDealsQuery(lookupFilter, manageOpen);
  const summaryColumns = [
    createDealStatusColumn(),
    createDealIdColumn(),
    createDealAuctionTypeColumn(),
    createDealFlightDatesColumn(),
    createDealBidFloorColumn(),
  ];
  const selectedColumns = buildDashboardDataTableColumns<Deal>([
    {
      id: 'name',
      header: 'Name',
      kind: DashboardDataTableColumnKind.Text,
      value: (deal) => deal.name || deal.id,
      headerClassName: 'min-w-[260px]',
      className: 'min-w-[260px] font-medium',
    },
    ...summaryColumns,
    createDealIntegrationsColumn(),
    createDealUpdatedColumn(),
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      header: 'Actions',
      cell: (deal) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove ${deal.name || deal.id}`}
          onClick={() => {
            setValue(
              'deals',
              selected.filter((candidate) => candidate.id !== deal.id),
              { shouldDirty: true },
            );
          }}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      ),
    },
  ]);

  // Long lists render one page of rows, not every row (keeps tab switches fast).
  const tablePage = useDashboardDataTableClientPage(selected);
  return (
    <DashboardPageFormBody width="lg">
      <Controller
        control={control}
        name="pmpEnabled"
        render={({ field }) => (
          <DashboardPageFormToggleSection
            label="Enabled"
            description="Enable private marketplace controls and assign eligible deals."
            checked={field.value}
            onCheckedChange={(checked) => {
              field.onChange(checked);
              if (!checked) setValue('privateAuction', false, { shouldDirty: true });
            }}
          >
            <span className="sr-only">PMP is enabled.</span>
          </DashboardPageFormToggleSection>
        )}
      />
      {enabled ? (
        <>
          <Controller
            control={control}
            name="privateAuction"
            render={({ field }) => (
              <DashboardPageFormToggleSection
                label="Private auction"
                description="When enabled, only impressions matching an assigned deal are eligible."
                checked={field.value}
                onCheckedChange={field.onChange}
              >
                <span className="sr-only">Private auction is enabled.</span>
              </DashboardPageFormToggleSection>
            )}
          />
          <DashboardDataTable
            title="Deals"
            tableLabel="Selected deals"
            columns={selectedColumns}
            items={tablePage.items}
            pagination={tablePage.pagination}
            getRowKey={(deal) => deal.id}
            emptyMessage="No deals selected."
            minWidthClassName="min-w-[1420px]"
            toolbar={
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setManageOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Manage deals
                </Button>
                {selected.length ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setValue('deals', [], { shouldDirty: true });
                    }}
                  >
                    Remove all
                  </Button>
                ) : null}
              </>
            }
          />
          <DashboardSelectionDialog
            open={manageOpen}
            title="Manage deals"
            description="Select deals available to this integration."
            items={query.data?.content ?? []}
            selected={selected}
            loading={query.isLoading || query.isFetching}
            error={query.isError}
            getId={(deal) => deal.id}
            getName={(deal) => deal.name || deal.id}
            initialPageSize={5}
            clientFilterKey={statuses.join(',')}
            matches={(deal) => statuses.length === 0 || statuses.includes(deal.status)}
            columns={summaryColumns}
            toolbarAfterSearch={(searchedItems) => {
              const counts = countByStatus(searchedItems);
              return (
                <DashboardTableMultiSelectFilter
                  ariaLabel="Filter deals by status"
                  label={
                    statuses.length ? statuses.map(dealStatusLabel).join(', ') : 'All statuses'
                  }
                  value={statuses}
                  options={Object.values(DealStatus).map((status) => ({
                    value: status,
                    label: dealStatusLabel(status),
                    count: counts[status] ?? 0,
                  }))}
                  onChange={setStatuses}
                />
              );
            }}
            searchLabel="Search deals"
            loadingMessage="Loading deals…"
            errorMessage="Deals could not be loaded."
            emptyMessage="No deals found."
            confirmLabel="Update deals"
            minWidthClassName="min-w-[1120px]"
            onOpenChange={setManageOpen}
            onConfirm={(deals) => {
              setValue('deals', deals, { shouldDirty: true });
            }}
          />
        </>
      ) : null}
    </DashboardPageFormBody>
  );
}
